<script setup lang="ts">
/**
 * Sélecteur de date des formulaires PUBLICS (adhésion, billetterie).
 *
 * Remplace le `<input type="date">` natif, dont l'apparence change d'un
 * navigateur à l'autre et jure avec le reste de la page (Karim 2026-09-21 :
 * « une jolie datepicker, pas un truc dégueulasse de base »).
 *
 * Le champ reste un vrai champ de formulaire : la valeur émise est une date
 * ISO `YYYY-MM-DD`, exactement ce que produisait l'input natif — rien à
 * changer côté enregistrement.
 *
 * Le champ est un BOUTON : un clic n'importe où sur la ligne ouvre le
 * calendrier, plus de saisie au clavier (Karim 2026-10-21). Pour aller vite
 * jusqu'à une date de naissance, le titre « juillet 1985 » est cliquable :
 * années → mois → jours, trois clics au lieu de remonter quarante ans.
 *
 * ⚠️ Le calendrier est sorti du flux (Teleport + `fixed`) : la carte participant
 * de l'adhésion est en `overflow: hidden` et le rognait (Karim 2026-10-21).
 * Placement par `placerListeSuggestions` (@donoor/utils, même règle que les
 * listes d'adresses) : sous le champ si la place y est, sinon au-dessus.
 */
import {
  placementListeInitial,
  placerListeSuggestions,
  zoneVisibleFenetre,
} from "@donoor/utils";
import { BORDURE_CHAMP_PUBLIC, BORDURE_CHAMP_PUBLIC_ERREUR } from "~/utils/stylesChamps";

const { t } = useI18n({ useScope: "local" });

const modele = defineModel<string | undefined>();

const props = withDefaults(
  defineProps<{
    placeholder?: string;
    /** Bordure rouge + `aria-invalid` quand le champ est en erreur. */
    enErreur?: boolean;
    /** Id posé sur le champ, pour que le label et le focus d'erreur le visent. */
    id?: string;
  }>(),
  { placeholder: undefined, enErreur: false, id: undefined },
);

const ouvert = ref(false);
const racine = ref<HTMLElement | null>(null);
const champ = ref<HTMLButtonElement | null>(null);
const calendrier = ref<HTMLElement | null>(null);

/** Expose le focus pour que la validation puisse ramener l'utilisateur ici. */
defineExpose({
  focus: () => champ.value?.focus(),
});

// ── Calendrier ─────────────────────────────────────────────────────────────
// Noms des mois et initiales des jours dans la langue de la page, via Intl.
// En français : « janvier »… « décembre » et « L M M J V S D », identiques à
// l'ancienne liste écrite à la main. 2024-01-01 est un lundi.
const { localeIntl } = useFormatLangue();
const MOIS = computed(() =>
  Array.from({ length: 12 }, (_, i) =>
    new Intl.DateTimeFormat(localeIntl.value, { month: "long" }).format(new Date(2000, i, 1)),
  ),
);
const MOIS_COURTS = computed(() =>
  Array.from({ length: 12 }, (_, i) =>
    new Intl.DateTimeFormat(localeIntl.value, { month: "short" }).format(new Date(2000, i, 1)),
  ),
);
const JOURS = computed(() =>
  Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(localeIntl.value, { weekday: "narrow" }).format(new Date(2024, 0, 1 + i)),
  ),
);

/** `YYYY-MM-DD` → Date locale à minuit, SANS fuseau (cf. dateSaisie.ts). */
function isoVersDate(iso: string | undefined): Date | null {
  const m = iso ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso) : null;
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date choisie au format de la langue (12/07/1985, 12.07.1985…). */
const texteAffiche = computed(() => {
  const d = isoVersDate(modele.value);
  return d
    ? new Intl.DateTimeFormat(localeIntl.value, { day: "2-digit", month: "2-digit", year: "numeric" }).format(d)
    : "";
});

const aujourdhui = new Date();
const curseur = ref(new Date(aujourdhui.getFullYear(), aujourdhui.getMonth(), 1));

/** `jours` : grille du mois ; `mois` : les 12 mois ; `annees` : liste défilante. */
const vue = ref<"jours" | "mois" | "annees">("jours");
const listeAnnees = ref<HTMLElement | null>(null);

// À l'ouverture : vue des jours, sur le mois de la date déjà choisie.
watch(ouvert, (o) => {
  if (!o) return;
  vue.value = "jours";
  majPosition();
  const d = isoVersDate(modele.value);
  if (d) curseur.value = new Date(d.getFullYear(), d.getMonth(), 1);
});

const titreMois = computed(
  () => `${MOIS.value[curseur.value.getMonth()]} ${curseur.value.getFullYear()}`,
);

/** De 1900 (dates de naissance) à dix ans devant (dates d'événement). */
const ANNEES = Array.from(
  { length: aujourdhui.getFullYear() + 10 - 1900 + 1 },
  (_, i) => aujourdhui.getFullYear() + 10 - i,
);
const anneeChoisie = computed(() => isoVersDate(modele.value)?.getFullYear() ?? null);
const moisChoisi = computed(() => {
  const d = isoVersDate(modele.value);
  return d && d.getFullYear() === curseur.value.getFullYear() ? d.getMonth() : null;
});

/** Vue des années : centrée sur l'année affichée, sans animation. */
async function ouvrirAnnees() {
  vue.value = "annees";
  await nextTick();
  const cible = listeAnnees.value?.querySelector<HTMLElement>(
    `[data-annee="${curseur.value.getFullYear()}"]`,
  );
  if (cible && listeAnnees.value) {
    listeAnnees.value.scrollTop =
      cible.offsetTop - listeAnnees.value.clientHeight / 2 + cible.clientHeight / 2;
  }
}
function choisirAnnee(annee: number) {
  curseur.value = new Date(annee, curseur.value.getMonth(), 1);
  vue.value = "mois";
}
function choisirMois(mois: number) {
  curseur.value = new Date(curseur.value.getFullYear(), mois, 1);
  vue.value = "jours";
}

/** Grille du mois : cases vides en tête pour aligner sur lundi. */
const cases = computed(() => {
  const annee = curseur.value.getFullYear();
  const mois = curseur.value.getMonth();
  const premier = new Date(annee, mois, 1);
  // getDay() : 0 = dimanche. On veut lundi en première colonne.
  const decalage = (premier.getDay() + 6) % 7;
  const nbJours = new Date(annee, mois + 1, 0).getDate();
  const out: Array<{ jour: number; iso: string } | null> = [];
  for (let i = 0; i < decalage; i++) out.push(null);
  for (let j = 1; j <= nbJours; j++) {
    const iso = `${annee}-${String(mois + 1).padStart(2, "0")}-${String(j).padStart(2, "0")}`;
    out.push({ jour: j, iso });
  }
  return out;
});

const isoAujourdhui = `${aujourdhui.getFullYear()}-${String(aujourdhui.getMonth() + 1).padStart(2, "0")}-${String(aujourdhui.getDate()).padStart(2, "0")}`;

function choisir(iso: string) {
  modele.value = iso;
  ouvert.value = false;
  champ.value?.focus();
}

function moisPrecedent() {
  curseur.value = new Date(curseur.value.getFullYear(), curseur.value.getMonth() - 1, 1);
}
function moisSuivant() {
  curseur.value = new Date(curseur.value.getFullYear(), curseur.value.getMonth() + 1, 1);
}
/** Flèches de la vue des mois : d'une année à l'autre. */
function anneePrecedente() {
  curseur.value = new Date(curseur.value.getFullYear() - 1, curseur.value.getMonth(), 1);
}
function anneeSuivante() {
  curseur.value = new Date(curseur.value.getFullYear() + 1, curseur.value.getMonth(), 1);
}

// ── Position du calendrier (sorti du flux) ─────────────────────────────────
// Recalculée à l'ouverture, au défilement et au redimensionnement (clavier
// mobile compris). Largeur fixe : on le garde dans l'écran sur petit mobile.
const LARGEUR_CALENDRIER = 280;
/** Grille de 6 semaines + en-tête : la hauteur réelle du calendrier ouvert. */
const HAUTEUR_CALENDRIER = 320;
const MARGE_ECRAN = 8;

const placement = ref(placementListeInitial());
const styleCalendrier = computed(() => {
  const p = placement.value;
  const style: Record<string, string> = {
    left: `${p.left}px`,
    width: `${LARGEUR_CALENDRIER}px`,
    maxHeight: `${p.hauteurMax}px`,
  };
  if (p.top !== null) style.top = `${p.top}px`;
  if (p.bottom !== null) style.bottom = `${p.bottom}px`;
  return style;
});
/** Glissement d'ouverture dans le sens où le calendrier pousse. */
const glissementFerme = computed(() =>
  placement.value.cote === "haut" ? "opacity-0 translate-y-1" : "opacity-0 -translate-y-1",
);

function majPosition() {
  const r = champ.value?.getBoundingClientRect();
  if (!r) return;
  const p = placerListeSuggestions(r, zoneVisibleFenetre(window), {
    hauteurMax: HAUTEUR_CALENDRIER,
    ecart: 8,
  });
  const leftMax = window.innerWidth - LARGEUR_CALENDRIER - MARGE_ECRAN;
  placement.value = { ...p, left: Math.max(MARGE_ECRAN, Math.min(p.left, leftMax)) };
}

function auDefilement() {
  if (ouvert.value) majPosition();
}

// Fermeture au clic extérieur (champ ET calendrier, qui vit dans <body>) et à Échap.
function auClicDehors(e: MouseEvent) {
  if (!ouvert.value) return;
  const cible = e.target as Node;
  if (racine.value?.contains(cible) || calendrier.value?.contains(cible)) return;
  ouvert.value = false;
}
function auClavier(e: KeyboardEvent) {
  if (e.key === "Escape" && ouvert.value) {
    ouvert.value = false;
    champ.value?.focus();
  }
}
onMounted(() => {
  document.addEventListener("mousedown", auClicDehors);
  document.addEventListener("keydown", auClavier);
  window.addEventListener("scroll", auDefilement, true);
  window.addEventListener("resize", auDefilement);
  window.visualViewport?.addEventListener("resize", auDefilement);
  window.visualViewport?.addEventListener("scroll", auDefilement);
});
onUnmounted(() => {
  document.removeEventListener("mousedown", auClicDehors);
  document.removeEventListener("keydown", auClavier);
  window.removeEventListener("scroll", auDefilement, true);
  window.removeEventListener("resize", auDefilement);
  window.visualViewport?.removeEventListener("resize", auDefilement);
  window.visualViewport?.removeEventListener("scroll", auDefilement);
});
</script>

<template>
  <div ref="racine" class="relative">
    <!-- Toute la ligne ouvre le calendrier : plus de saisie au clavier. -->
    <button
      :id="props.id"
      ref="champ"
      type="button"
      aria-haspopup="dialog"
      :aria-expanded="ouvert"
      :aria-invalid="props.enErreur || undefined"
      class="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-white dark:bg-[#0f0f0f] text-sm text-left transition-colors cursor-pointer"
      :class="props.enErreur ? BORDURE_CHAMP_PUBLIC_ERREUR : BORDURE_CHAMP_PUBLIC"
      @click="ouvert = !ouvert"
    >
      <span :class="texteAffiche ? 'text-[#2d2e5c] dark:text-white' : 'text-gray-400'">
        {{ texteAffiche || (props.placeholder ?? t('champ.placeholder')) }}
      </span>
      <UIcon name="i-lucide-calendar" class="size-4 shrink-0 text-gray-400" />
    </button>

    <Teleport to="body">
    <Transition
      enter-active-class="transition duration-150 ease-out"
      :enter-from-class="glissementFerme"
      leave-active-class="transition duration-100 ease-in"
      :leave-to-class="glissementFerme"
    >
      <div
        v-if="ouvert"
        ref="calendrier"
        role="dialog"
        class="fixed z-[9999] p-3 rounded-2xl border border-gray-200 dark:border-[#262626] bg-white dark:bg-[#0f0f0f] shadow-xl overflow-y-auto overscroll-contain"
        :style="styleCalendrier"
        :data-cote="placement.cote"
        :data-vue="vue"
      >
        <!-- ── Jours ── titre cliquable → années. -->
        <template v-if="vue === 'jours'">
          <div class="flex items-center justify-between mb-3">
            <button type="button" :aria-label="$t('association.champsFormulaire.moisPrecedent')" class="p-1.5 rounded-lg text-gray-400 hover:text-[#2d2e5c] dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1a1a1a]" @click="moisPrecedent">
              <UIcon name="i-lucide-chevron-left" class="size-4" />
            </button>
            <button
              type="button"
              :aria-label="t('calendrier.choisirMoisAnnee')"
              class="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-sm font-semibold text-[#2d2e5c] dark:text-white capitalize hover:bg-gray-100 dark:hover:bg-[#1a1a1a] transition-colors"
              @click="ouvrirAnnees"
            >
              {{ titreMois }}
              <UIcon name="i-lucide-chevron-down" class="size-3.5 text-gray-400" />
            </button>
            <button type="button" :aria-label="$t('association.champsFormulaire.moisSuivant')" class="p-1.5 rounded-lg text-gray-400 hover:text-[#2d2e5c] dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1a1a1a]" @click="moisSuivant">
              <UIcon name="i-lucide-chevron-right" class="size-4" />
            </button>
          </div>

          <div class="grid grid-cols-7 gap-0.5 mb-1">
            <span
              v-for="(j, i) in JOURS"
              :key="i"
              class="h-7 flex items-center justify-center text-[11px] font-medium text-gray-400"
            >{{ j }}</span>
          </div>

          <div class="grid grid-cols-7 gap-0.5">
            <template v-for="(c, i) in cases" :key="i">
              <span v-if="!c" />
              <button
                v-else
                type="button"
                class="h-8 rounded-lg text-sm transition-colors"
                :class="modele === c.iso
                  ? 'bg-[#212121] dark:bg-white text-white dark:text-[#212121] font-semibold'
                  : c.iso === isoAujourdhui
                    ? 'text-[#2d2e5c] dark:text-white font-semibold ring-1 ring-inset ring-gray-300 dark:ring-[#3a3a3a] hover:bg-gray-100 dark:hover:bg-[#1a1a1a]'
                    : 'text-[#2d2e5c] dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#1a1a1a]'"
                @click="choisir(c.iso)"
              >
                {{ c.jour }}
              </button>
            </template>
          </div>
        </template>

        <!-- ── Mois de l'année affichée ── titre cliquable → années. -->
        <template v-else-if="vue === 'mois'">
          <div class="flex items-center justify-between mb-3">
            <button type="button" :aria-label="$t('association.champsFormulaire.anneePrecedente')" class="p-1.5 rounded-lg text-gray-400 hover:text-[#2d2e5c] dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1a1a1a]" @click="anneePrecedente">
              <UIcon name="i-lucide-chevron-left" class="size-4" />
            </button>
            <button
              type="button"
              :aria-label="t('calendrier.choisirAnnee')"
              class="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-sm font-semibold text-[#2d2e5c] dark:text-white hover:bg-gray-100 dark:hover:bg-[#1a1a1a] transition-colors"
              @click="ouvrirAnnees"
            >
              {{ curseur.getFullYear() }}
              <UIcon name="i-lucide-chevron-down" class="size-3.5 text-gray-400" />
            </button>
            <button type="button" :aria-label="$t('association.champsFormulaire.anneeSuivante')" class="p-1.5 rounded-lg text-gray-400 hover:text-[#2d2e5c] dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1a1a1a]" @click="anneeSuivante">
              <UIcon name="i-lucide-chevron-right" class="size-4" />
            </button>
          </div>

          <div class="grid grid-cols-3 gap-1.5">
            <button
              v-for="(nom, i) in MOIS_COURTS"
              :key="i"
              type="button"
              class="h-11 rounded-lg text-sm capitalize transition-colors"
              :class="moisChoisi === i
                ? 'bg-[#212121] dark:bg-white text-white dark:text-[#212121] font-semibold'
                : 'text-[#2d2e5c] dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#1a1a1a]'"
              @click="choisirMois(i)"
            >
              {{ nom }}
            </button>
          </div>
        </template>

        <!-- ── Années : liste défilante, centrée sur l'année affichée. -->
        <template v-else>
          <p class="mb-2 text-center text-sm font-semibold text-[#2d2e5c] dark:text-white">
            {{ t('calendrier.choisirAnnee') }}
          </p>
          <div ref="listeAnnees" class="relative h-[232px] overflow-y-auto overscroll-contain">
            <div class="grid grid-cols-4 gap-1.5">
              <button
                v-for="annee in ANNEES"
                :key="annee"
                type="button"
                :data-annee="annee"
                class="h-10 rounded-lg text-sm tabular-nums transition-colors"
                :class="anneeChoisie === annee
                  ? 'bg-[#212121] dark:bg-white text-white dark:text-[#212121] font-semibold'
                  : annee === curseur.getFullYear()
                    ? 'text-[#2d2e5c] dark:text-white font-semibold ring-1 ring-inset ring-gray-300 dark:ring-[#3a3a3a] hover:bg-gray-100 dark:hover:bg-[#1a1a1a]'
                    : 'text-[#2d2e5c] dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#1a1a1a]'"
                @click="choisirAnnee(annee)"
              >
                {{ annee }}
              </button>
            </div>
          </div>
        </template>
      </div>
    </Transition>
    </Teleport>
  </div>
</template>

<i18n lang="json" src="../../../i18n/pages/components/form-fields/FieldDatePicker.json"></i18n>
